import { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import clsx from 'clsx';

import { useAppAbility, useTenantSession } from '@/features/auth';
import { ShellHeaderProvider } from '@/shared/shell/ShellHeaderContext';
import Notice from '@/shared/ui/Notice';
import { controlClass, labelClass } from '@/shared/ui/formClasses';
import {
  getNavigationSections,
  getVisibleNavigation,
  isNavigationItemActive,
} from '@/app/layout/navigation';
import SessionSidebar from '@/app/layout/SessionSidebar';
import AppShellHeader from '@/app/layout/AppShellHeader';
import { isCompactShellRoutePath } from './shellRouteUtils';
import { isTenantlessPath } from './tenantlessRoutes';

const Layout = () => {
  const location = useLocation();
  const ability = useAppAbility();
  const {
    activeTenantId,
    activeTenant,
    availableTenants,
    needsTenantSelection,
    tenantsLoading,
    autoSwitching: tenantAutoSwitching,
    error: tenantsError,
    switchTenant: handleSwitchTenant,
  } = useTenantSession();

  const [isSidebarPinned, setIsSidebarPinned] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const isTenantlessPage = isTenantlessPath(location.pathname) && ability.can('read', 'Tenant');
  const shouldBlockTenantScopedUX = needsTenantSelection && !isTenantlessPage;
  const isCompactShellRoute = isCompactShellRoutePath(location.pathname);
  const navigationSections = useMemo(() => getNavigationSections(ability), [ability]);
  const visibleNavigation = useMemo(() => getVisibleNavigation(ability), [ability]);

  const activeItem = useMemo(
    () =>
      visibleNavigation.find((item) => isNavigationItemActive(location.pathname, item)) ||
      visibleNavigation[0],
    [location.pathname, visibleNavigation]
  );

  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location.pathname]);

  const isSidebarExpanded = isSidebarPinned;

  return (
    <div className="min-h-screen bg-canvas text-strong">
      <SessionSidebar
        navigationSections={navigationSections}
        activeTenant={activeTenant}
        availableTenants={availableTenants}
        onSwitchTenant={handleSwitchTenant}
        isMobileOpen={isMobileSidebarOpen}
        onMobileClose={() => setIsMobileSidebarOpen(false)}
        isPinned={isSidebarPinned}
        onPinnedChange={setIsSidebarPinned}
        tenantsLoading={tenantsLoading}
        tenantAutoSwitching={tenantAutoSwitching}
      />

      <div
        className={clsx(
          'min-h-screen transition-[padding-left] duration-200 ease-out',
          isSidebarPinned ? 'lg:pl-70' : 'lg:pl-16'
        )}
      >
        <ShellHeaderProvider
          fallbackTitle={activeItem.label}
          fallbackSubtitle={activeItem.description}
        >
          <AppShellHeader
            isSidebarPinned={isSidebarPinned}
            isMobileSidebarOpen={isMobileSidebarOpen}
            onToggleSidebar={() => setIsSidebarPinned((current) => !current)}
            onToggleMobile={() => setIsMobileSidebarOpen((current) => !current)}
          />

          {needsTenantSelection || tenantsError ? (
            <div className="px-4 pt-4 md:px-6 lg:px-8">
              <div className="space-y-2">
                {needsTenantSelection ? (
                  <Notice tone="warning">
                    {tenantAutoSwitching
                      ? 'Selezione tenant in corso…'
                      : 'Seleziona un tenant operativo per accedere ai dati della flotta.'}
                  </Notice>
                ) : null}
                {tenantsError ? <Notice tone="danger">{tenantsError}</Notice> : null}
              </div>
            </div>
          ) : null}

          <main
            className={
              isCompactShellRoute ? 'px-2 py-1 lg:px-3' : 'px-4 py-5 md:px-6 lg:px-8 lg:py-7'
            }
          >
            {shouldBlockTenantScopedUX ? (
              <div className="mx-auto max-w-xl rounded-hero border border-default bg-default px-6 py-8 text-center shadow-resting">
                <p className="text-eyebrow text-link">Accesso dati</p>
                <h2 className="mt-2 text-hero-title text-strong">Seleziona tenant</h2>
                <p className="mt-3 text-body text-muted">
                  Prima di entrare nei moduli operativi serve scegliere il tenant corretto.
                </p>

                {Array.isArray(availableTenants) && availableTenants.length > 0 ? (
                  <div className="mt-6 text-left">
                    <label htmlFor="tenant-select" className={labelClass}>
                      Tenant operativo
                    </label>
                    <select
                      id="tenant-select"
                      value={activeTenantId}
                      onChange={(event) => handleSwitchTenant(event.target.value)}
                      disabled={tenantsLoading || tenantAutoSwitching}
                      className={controlClass}
                    >
                      <option value="" disabled>
                        Seleziona tenant…
                      </option>
                      {availableTenants.map((tenant) => (
                        <option key={tenant._id} value={tenant._id}>
                          {tenant.name}
                          {tenant.slug ? ` (${tenant.slug})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <p className="mt-6 text-sm text-subtle">
                    Nessun tenant disponibile per questo utente.
                  </p>
                )}
              </div>
            ) : (
              <Outlet
                context={{
                  isSidebarExpanded,
                  setIsSidebarExpanded: setIsSidebarPinned,
                  setIsMobileSidebarOpen,
                }}
              />
            )}
          </main>
        </ShellHeaderProvider>
      </div>
    </div>
  );
};

export default Layout;
